import { Button, CircularProgress, IconButton, Tooltip } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CircularLinkedList, Node } from "../classes/CircularLinkedList";
import usePopulation from "../hooks/usePopulation";
import useZoom from "../hooks/useZoom";
import IndividualType from "../types/individual";
import ZoomModal from "./modals/ZoomModal";
import Individual from './Individual';
import ResetPopulation from "./ResetPopulation";
import useModal from "../hooks/useModal";
import HelpModal from "./modals/HelpModal";
import { QuestionMark } from "@mui/icons-material";

interface Props {
  disableKeyboardShortcuts: boolean;
  openUserModal: () => void;
  refreshUsage: () => void;
}

export default function Population({
  disableKeyboardShortcuts,
  openUserModal,
  refreshUsage,
} : Props) {
  const {
    population,
    toggleSelected,
    isSelected,
    setImageUrl,
    setGenerationError,
    initPopulation,
    updatePopulation,
    isReady,
  } = usePopulation();

  const [focusedNode, setFocusedNode] = useState<Node<IndividualType> | null>(null);
  const { zoomOpen, carouselNode, setCarouselNode, showZoom, closeZoom } = useZoom();
  const { modalOpen: helpOpen, showModal: showHelp, closeModal: closeHelp } = useModal();

  const numSelected = useMemo(
    () => population
      ? population.individuals.filter(({ selected }) => selected).length
      : 0,
    [population]
  );

  // how many have either generated, or DALL-E returned an error
  const numGenerated = useMemo(
    () => population
      ? population.individuals.filter(({ imageUrl, generationError }) => imageUrl || generationError).length
      : 0,
    [population]
  );

  const notOkToUpdate = population == null || numSelected < 2 || numGenerated < population.size;

  const handleUpdate = useCallback(async () => {
    if (notOkToUpdate) return;
    setFocusedNode(null);
    await updatePopulation();
  }, [notOkToUpdate, updatePopulation, setFocusedNode]);

  const carousel = useMemo(() => {
    if (population == null) return;
    const carousel = new CircularLinkedList<IndividualType>();
    population?.individuals.forEach((individual) => carousel.append(individual));
    return carousel;
  }, [population]);

  // refresh focusedNode and carouselNode (with new next/prev neighbours) after population (+ carousel) changes
  useEffect(() => {
    if (carousel) {
      setFocusedNode((prev) => {
        if (!prev) return null;
        return carousel.head?.findRight(({ id }) => id === prev.data.id) ?? null;
      });
      setCarouselNode((prev) => {
        if (!prev) return null;
        return carousel.head?.findRight(({ id }) => id === prev.data.id) ?? null;
      });
    }
  }, [carousel, setFocusedNode, setCarouselNode])

  const handleNextImage = useCallback(
    () => setFocusedNode(focusedNode?.next?.findRight(({ imageUrl }) => !!imageUrl) ?? carousel?.head ?? null),
    [focusedNode, setFocusedNode, carousel]
  );
  const handlePrevImage = useCallback(
    () => setFocusedNode(focusedNode?.prev?.findLeft(({ imageUrl }) => !!imageUrl) ?? carousel?.head ?? null),
    [focusedNode, setFocusedNode, carousel]
  );

  const handleToggleSelectedOnFocused = useCallback(() => {
    if (focusedNode == null) return;
    // update state
    toggleSelected(focusedNode?.data.id);
  }, [toggleSelected, focusedNode]);

  // Enter key to open zoom modal at first image
  // or regenerate images if some are selected
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.code === 'ArrowRight') {
      handleNextImage();
    } else if (event.code === 'ArrowLeft') {
      handlePrevImage();
    } else if (event.code === 'Space') {
      event.preventDefault();
      handleToggleSelectedOnFocused();
    } else if (event.code === 'Enter' && !helpOpen) {
      event.preventDefault();
      if (numGenerated === population?.size) {
        if (numSelected >= 2)
          handleUpdate();
        else if (focusedNode != null)
          showZoom(focusedNode);
        else if (carousel?.head != null)
          showZoom(carousel.head);
      }
    } else if (event.code === 'KeyZ' && !helpOpen) { // zoom any time with no update
      if (focusedNode != null)
        showZoom(focusedNode);
      else if (carousel?.head != null)
        showZoom(carousel.head);
    } else if (event.code === 'Escape') { // cancel focus
      setFocusedNode(null);
    } else if (event.code === 'KeyH' && !zoomOpen) {
      event.preventDefault();
      showHelp();
    }
  }, [
    showZoom, carousel, numGenerated, population?.size, numSelected, focusedNode,
    setFocusedNode, handleUpdate, handleNextImage, handlePrevImage, handleToggleSelectedOnFocused,
    showHelp, helpOpen, zoomOpen,
  ]);

  // only listen for the event if the zoom modal isn't open
  useEffect(() => {
    if (!disableKeyboardShortcuts && !zoomOpen)
      window.addEventListener('keydown', handleKeyDown);
    return () => {
      if (!disableKeyboardShortcuts && !zoomOpen)
        window.removeEventListener('keydown', handleKeyDown);
    };
  }, [disableKeyboardShortcuts, handleKeyDown, zoomOpen]);

  if (!isReady)
    return <div className="population-container"><div className="population-spinner">Retrieving your data...</div></div>;

  return (
    <div className="population-container">
      {population == null ?
        <div className="population-spinner">
          <Button
            onClick={initPopulation}
          >
            Get started!
          </Button>
        </div>
        :
        population.individuals.length === 0 ?
          <div className="population-spinner">
            <CircularProgress />
          </div>
          :
          <>
            <div className="heading-container">
              <Tooltip title={numGenerated < population.size ? "Please wait for the images to be generated..." : numSelected < 2 ? "Click on two or more images first!" : "Click to generate new images..."}>
                <span>
                  <Button
                    color="success"
                    size="large"
                    disabled={notOkToUpdate}
                    onClick={handleUpdate}
                    sx={{ fontWeight: 'bold', borderRadius: '20px' }}
                  >
                    Generate images
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Start again with a new set of images">
                <span>
                  <ResetPopulation
                    onConfirm={initPopulation}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Get help...">
                <span>
                  <IconButton
                    onClick={showHelp}
                  >
                    <QuestionMark />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
            <div className="population">
              {
                population.individuals.map(
                  (individual, index) => {
                    return <Individual
                      key={individual.id}
                      toggleSelected={toggleSelected}
                      setImageUrl={setImageUrl}
                      setGenerationError={setGenerationError}
                      showZoom={showZoom}
                      carouselNode={carousel?.getNodeAt(index) ?? null}
		      isFocused={focusedNode != null && focusedNode.data.id === individual.id}
		      refreshUsage={refreshUsage}
                      {...individual}
                    />;
                  }
                )
              }
            </div>
          </>
      }
      <ZoomModal
        open={zoomOpen}
        onClose={closeZoom}
        carouselNode={carouselNode}
        setCarouselNode={setCarouselNode}
        toggleSelected={toggleSelected}
        isSelected={isSelected}
        updatePopulation={handleUpdate}
      />
      <HelpModal
        open={population == null || helpOpen}
        onClose={closeHelp}
        onGetStarted={population == null ? initPopulation : undefined}
	openUserModal={openUserModal}
      />
    </div >
  );
}
